// StreamingMessage.tsx – Progressively renders streamed text with a blinking cursor.
import React from 'react';
import BlinkingCursor from './BlinkingCursor';
import type { JSX } from 'react';

interface StreamingMessageProps {
    /** Accumulated text received so far from chunk events */
    text: string;
    /** Whether streaming is still active (shows cursor) */
    isStreaming: boolean;
}

/** Format text: bold, linkify emails and phone numbers */
const formatText = (input: string): string => {
    // Bold: **text** → <strong>
    let result = input.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Email links
    result = result.replace(
        /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
        '<a href="mailto:$1" class="chat-link chat-link-email">$1</a>'
    );
    // Phone links
    result = result.replace(
        /(\+?\d{1,3}[\s-]?)?(\(?\d{3}\)?[\s-]?\d{3}[\s-]?\d{4})/g,
        (match) => {
            const number = match.replace(/[^\d+]/g, '');
            return `<a href="tel:${number}" class="chat-link chat-link-phone">${match}</a>`;
        }
    );
    return result;
};

/**
 * Renders progressively streamed bot text.
 * While streaming, shows a BlinkingCursor at the end.
 * Once complete (isStreaming=false), the cursor disappears.
 */
const StreamingMessage: React.FC<StreamingMessageProps> = ({ text, isStreaming }) => {
    // Render as bullet list if text contains newlines or bullets
    const renderContent = (): JSX.Element => {
        if (text.includes("\n") || text.includes("•")) {
            const items = text.split(/\n|•/)
                .map(item => item.replace(/^[*-]\s+/, '').trim())
                .filter(item => item.length > 0);
            return (
                <ul className="list-unstyled ms-3">
                    {items.map((item, idx) => (
                        <li key={idx} className="mb-1" dangerouslySetInnerHTML={{ __html: formatText(item) }} />
                    ))}
                </ul>
            );
        }
        return <span dangerouslySetInnerHTML={{ __html: formatText(text) }} />;
    };

    return (
        <div className="streaming-message-wrap">
            <div className="bubble bot-bubble streaming-bubble">
                <div className="streaming-text">
                    {renderContent()}
                    {isStreaming && <BlinkingCursor />}
                </div>
            </div>
        </div>
    );
};

export default React.memo(StreamingMessage);
